import {
  listMaskingJobs,
  getLatestDataSourceInventory,
  getSensitiveFieldCountFromInventory,
  inventoryContentOf,
} from './api';

/**
 * Masking jobs over time for a data source, shaped for the shared TimeSeriesChart
 * (mirrors getSearchHistogram in SensitiveDataLandscape/LandscapeUtils).
 *
 * The masking job DTO does not carry a per-job masked-field count, so we use the current
 * saved-inventory masked-field count as a proxy bar height for every job. This is a known
 * approximation — the timeline shows *when* masking ran, with a flat field count.
 */
const getMaskingHistogram = async (dataSourceId) => {
  const numericId = Number(dataSourceId);
  if (!Number.isFinite(numericId)) return [];

  const res = await listMaskingJobs({ dataSourceId: numericId, page: 0, size: 500, sort: ['id,asc'] });
  const jobs = res && Array.isArray(res.content) ? res.content : [];

  let maskedFieldCount = 0;
  try {
    const inventory = await getLatestDataSourceInventory(numericId);
    maskedFieldCount = getSensitiveFieldCountFromInventory(inventoryContentOf(inventory));
  } catch (e) {
    maskedFieldCount = 0;
  }

  return jobs
    .filter((job) => job && job.id != null)
    .sort((a, b) => {
      const timeA = Date.parse(a.finishedAt || a.startedAt || 0);
      const timeB = Date.parse(b.finishedAt || b.startedAt || 0);
      return timeA - timeB;
    })
    .map((job) => {
      const startMs = Date.parse(job.startedAt);
      const finishMs = Date.parse(job.finishedAt);
      const durationMs =
        Number.isFinite(startMs) && Number.isFinite(finishMs) && finishMs >= startMs
          ? finishMs - startMs
          : null;
      return {
        date: job.finishedAt || job.startedAt || new Date().toISOString(),
        sensitiveFieldCount: maskedFieldCount,
        // recordsProcessed is the worker's masked-row count for the job (may be null for older jobs).
        rowsMasked: job.recordsProcessed != null ? Number(job.recordsProcessed) : null,
        durationMs,
        searchids: [String(job.id)],
        registeredServiceId: job.registeredServiceId || null,
      };
    });
};

/** Mean masking-job wall-clock time (ms) over jobs with a known start+finish; null when none. */
const getAverageMaskingTimeMs = (timeSeriesData) => {
  const durations = (timeSeriesData || [])
    .map((p) => p && p.durationMs)
    .filter((d) => Number.isFinite(d) && d >= 0);
  if (durations.length === 0) return null;
  return durations.reduce((sum, d) => sum + d, 0) / durations.length;
};

export { getMaskingHistogram, getAverageMaskingTimeMs };
